<template>
  <div class="admin-schedules">
    <!-- 搜索和筛选区域 -->
    <div class="search-section">
      <el-card>
        <div class="search-form">
          <el-row :gutter="20">
            <el-col :span="8">
              <el-select v-model="searchForm.session_id" placeholder="选择 Session" clearable style="width: 100%" @change="handleSessionChange">
                <el-option
                  v-for="session in sessionOptions"
                  :key="session.id"
                  :label="`${formatDate(session.session_date)} - ${getSessionTypeText(session.session_type)}`"
                  :value="session.id"
                />
              </el-select>
            </el-col>
            <el-col :span="8">
              <el-input v-model="searchForm.keyword" placeholder="搜索标题或演讲者" clearable @keyup.enter="handleSearch">
                <template #prefix>
                  <el-icon><Search /></el-icon>
                </template>
              </el-input>
            </el-col>
            <el-col :span="8">
              <el-button type="primary" @click="handleSearch">
                <el-icon><Search /></el-icon>
                搜索
              </el-button>
              <el-button @click="handleReset">
                <el-icon><Refresh /></el-icon>
                重置
              </el-button>
            </el-col>
          </el-row>
        </div>
      </el-card>
    </div>

    <!-- 操作按钮区域 -->
    <div class="action-section">
      <el-card>
        <div class="action-buttons">
          <el-button type="primary" @click="handleAdd">
            <el-icon><Plus /></el-icon>
            新增日程
          </el-button>
          <el-button type="success" @click="handleRefresh">
            <el-icon><Refresh /></el-icon>
            刷新
          </el-button>
        </div>
      </el-card>
    </div>

    <!-- 数据表格 -->
    <div class="table-section">
      <el-card>
        <el-table v-loading="loading" :data="filteredTableData" stripe border style="width: 100%" empty-text="暂无数据">
          <el-table-column prop="id" label="ID" width="80" align="center" />
          <el-table-column prop="session_info" label="所属 Session" width="200">
            <template #default="{ row }">
              <div v-if="row.session">
                <el-tag :type="getSessionTypeTag(row.session.session_type)" size="small">
                  {{ getSessionTypeText(row.session.session_type) }}
                </el-tag>
                <div class="session-date">{{ formatDate(row.session.session_date) }}</div>
              </div>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column prop="time_range" label="时间" width="150" align="center">
            <template #default="{ row }">
              <div class="time-range">
                <span class="start-time">{{ formatTime(row.start_time) }}</span>
                <span v-if="row.end_time" class="time-separator">-</span>
                <span v-if="row.end_time" class="end-time">{{ formatTime(row.end_time) }}</span>
              </div>
              <div v-if="row.time_description" class="time-desc pre-wrap">{{ row.time_description }}</div>
            </template>
          </el-table-column>
          <el-table-column prop="title" label="标题" min-width="250">
            <template #default="{ row }">
              <div class="title-text pre-wrap">{{ row.title }}</div>
            </template>
          </el-table-column>
          <el-table-column prop="speaker_name" label="演讲者" width="150">
            <template #default="{ row }">
              <div class="pre-wrap">{{ row.speaker_name || '-' }}</div>
            </template>
          </el-table-column>
          <el-table-column prop="sort_order" label="排序" width="80" align="center">
            <template #default="{ row }">
              <el-tag :type="row.sort_order ? 'primary' : 'info'" size="small">
                {{ row.sort_order || 0 }}
              </el-tag>
            </template>
          </el-table-column>
          <!-- <el-table-column prop="created_at" label="创建时间" width="180" align="center">
            <template #default="{ row }">
              {{ formatDateTime(row.created_at) }}
            </template>
          </el-table-column> -->
          <el-table-column label="操作" width="200" align="center" fixed="right">
            <template #default="{ row }">
              <el-button type="primary" size="small" @click="handleEdit(row)">编辑</el-button>
              <el-button type="danger" size="small" @click="handleDelete(row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-section">
          <el-pagination
            v-model:current-page="pagination.page"
            v-model:page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            :total="pagination.total"
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </el-card>
    </div>

    <!-- 新增/编辑弹窗 -->
    <el-dialog
      v-model="formVisible"
      :title="isEdit ? '编辑日程' : '新增日程'"
      width="600px"
      :close-on-click-modal="false"
    >
      <el-form ref="formRef" :model="form" :rules="formRules" label-width="100px">
        <el-form-item label="Session" prop="session_id">
          <el-select v-model="form.session_id" placeholder="选择 Session" style="width: 100%">
            <el-option
              v-for="session in sessionOptions"
              :key="session.id"
              :label="`${formatDate(session.session_date)} - ${getSessionTypeText(session.session_type)}`"
              :value="session.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="开始时间" prop="start_time">
          <el-time-picker
            v-model="form.start_time"
            placeholder="选择开始时间"
            format="HH:mm"
            value-format="HH:mm"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="结束时间" prop="end_time">
          <el-time-picker
            v-model="form.end_time"
            placeholder="选择结束时间（可选）"
            format="HH:mm"
            value-format="HH:mm"
            style="width: 100%"
            clearable
          />
        </el-form-item>
        <el-form-item label="时间描述" prop="time_description">
          <el-input
            v-model="form.time_description"
            type="textarea"
            :rows="2"
            placeholder="如：(EST, 21/11/2025, 9:35-10:00 PM)"
          />
        </el-form-item>
        <el-form-item label="标题" prop="title">
          <el-input
            v-model="form.title"
            type="textarea"
            :rows="2"
            placeholder="请输入日程标题"
          />
        </el-form-item>
        <el-form-item label="演讲者" prop="speaker_name">
          <el-input
            v-model="form.speaker_name"
            type="textarea"
            :rows="2"
            placeholder="请输入演讲者姓名（可选）"
          />
        </el-form-item>
        <el-form-item label="演讲者介绍" prop="speaker_bio">
          <el-input
            v-model="form.speaker_bio"
            type="textarea"
            :rows="3"
            placeholder="请输入演讲者介绍（可选）"
          />
        </el-form-item>
        <el-form-item label="排序序号" prop="sort_order">
          <el-input-number v-model="form.sort_order" :min="0" :max="9999" :step="1" style="width: 100%" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="formVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSave" :loading="saving">保存</el-button>
      </template>
    </el-dialog>

    <!-- 详情弹窗 -->
    <el-dialog v-model="detailVisible" title="日程详情" width="700px" :close-on-click-modal="false">
      <div v-if="currentRecord" class="detail-content">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="ID">{{ currentRecord.id }}</el-descriptions-item>
          <el-descriptions-item label="排序序号">{{ currentRecord.sort_order || 0 }}</el-descriptions-item>
          <el-descriptions-item label="所属 Session" :span="2">
            <div v-if="currentRecord.session">
              <el-tag :type="getSessionTypeTag(currentRecord.session.session_type)" size="small">
                {{ getSessionTypeText(currentRecord.session.session_type) }}
              </el-tag>
              <span class="session-date" style="margin-left: 10px;">{{ formatDate(currentRecord.session.session_date) }}</span>
              <span v-if="currentRecord.session.host" class="session-host" style="margin-left: 10px; color: #666;">
                主持人：{{ currentRecord.session.host }}
              </span>
            </div>
          </el-descriptions-item>
          <el-descriptions-item label="时间">
            {{ formatTime(currentRecord.start_time) }}
            <span v-if="currentRecord.end_time"> - {{ formatTime(currentRecord.end_time) }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="时间描述">
            <div class="pre-wrap">{{ currentRecord.time_description || '-' }}</div>
          </el-descriptions-item>
          <el-descriptions-item label="标题" :span="2">
            <div class="pre-wrap">{{ currentRecord.title }}</div>
          </el-descriptions-item>
          <el-descriptions-item label="演讲者">
            <div class="pre-wrap">{{ currentRecord.speaker_name || '-' }}</div>
          </el-descriptions-item>
          <el-descriptions-item label="创建时间">{{ formatDateTime(currentRecord.created_at) }}</el-descriptions-item>
          <el-descriptions-item label="演讲者介绍" :span="2">
            <div class="speaker-bio">{{ currentRecord.speaker_bio || '-' }}</div>
          </el-descriptions-item>
        </el-descriptions>
      </div>
      <template #footer>
        <el-button @click="detailVisible = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Refresh, Search } from '@element-plus/icons-vue'
import { getSchedules, createSchedule, updateSchedule, deleteSchedule } from '@/api/schedule'
import { getSessions } from '@/api/session'

export default {
  name: 'AdminSchedules',
  components: {
    Plus,
    Refresh,
    Search
  },
  setup() {
    // 响应式数据
    const loading = ref(false)
    const saving = ref(false)
    const tableData = ref([])
    const sessionOptions = ref([])
    const formVisible = ref(false)
    const detailVisible = ref(false)
    const isEdit = ref(false)
    const currentRecord = ref(null)
    const formRef = ref(null)

    // 搜索表单
    const searchForm = reactive({
      session_id: null,
      keyword: ''
    })

    // 表单数据
    const form = reactive({
      session_id: null,
      start_time: '',
      end_time: '',
      time_description: '',
      title: '',
      speaker_name: '',
      speaker_bio: '',
      sort_order: 0
    })

    // 表单验证规则
    const formRules = {
      session_id: [
        { required: true, message: '请选择所属 Session', trigger: 'change' }
      ],
      start_time: [
        { required: true, message: '请选择开始时间', trigger: 'change' }
      ]
      // 标题现在可以为空，不需要验证
    }

    // 分页数据
    const pagination = reactive({
      page: 1,
      pageSize: 20,
      total: 0
    })

    // 过滤后的数据
    const filteredTableData = computed(() => {
      let data = [...tableData.value]
      
      // 按 Session 过滤
      if (searchForm.session_id) {
        data = data.filter(item => item.session_id === searchForm.session_id)
      }
      
      // 按关键词过滤
      if (searchForm.keyword) {
        const keyword = searchForm.keyword.toLowerCase()
        data = data.filter(item => 
          (item.title && item.title.toLowerCase().includes(keyword)) ||
          (item.speaker_name && item.speaker_name.toLowerCase().includes(keyword))
        )
      }
      
      // 更新总数
      pagination.total = data.length
      
      // 分页
      const start = (pagination.page - 1) * pagination.pageSize
      const end = start + pagination.pageSize
      return data.slice(start, end)
    })

    // 获取数据
    const fetchData = async () => {
      loading.value = true
      try {
        const [schedulesRes, sessionsRes] = await Promise.all([
          getSchedules({ sort_by: 'start_time', order: 'asc' }),
          getSessions({ sort_by: 'session_date', order: 'desc' })
        ])

        if (schedulesRes.success) {
          // 为每个日程添加 session 信息
          const schedules = schedulesRes.data
          if (sessionsRes.success) {
            // 后端返回的是按日期分组的数据结构，需要扁平化为 session 数组
            const sessionList = []
            sessionsRes.data.forEach(dateGroup => {
              if (dateGroup.sessions) {
                Object.values(dateGroup.sessions).forEach(session => {
                  if (session) {
                    sessionList.push(session)
                  }
                })
              }
            })
            sessionOptions.value = sessionList
            const sessionMap = {}
            sessionList.forEach(session => {
              sessionMap[session.id] = session
            })
            schedules.forEach(schedule => {
              schedule.session = sessionMap[schedule.session_id]
            })
          }
          tableData.value = schedules
        } else {
          ElMessage.error(schedulesRes.message || '获取数据失败')
        }
      } catch (error) {
        console.error('获取日程列表失败:', error)
        ElMessage.error('获取数据失败')
      } finally {
        loading.value = false
      }
    }

    // 获取 Session 类型文本
    const getSessionTypeText = (type) => {
      const typeMap = {
        'OPENING_SESSION': 'OPENING SESSION',
        'MORNING_SESSION': 'MORNING SESSION',
        'AFTERNOON_SESSION': 'AFTERNOON SESSION'
      }
      return typeMap[type] || type
    }

    // 获取 Session 类型标签样式
    const getSessionTypeTag = (type) => {
      const typeMap = {
        'OPENING_SESSION': 'success',
        'MORNING_SESSION': 'primary',
        'AFTERNOON_SESSION': 'warning'
      }
      return typeMap[type] || 'info'
    }

    // 搜索
    const handleSearch = () => {
      pagination.page = 1
    }

    // 重置
    const handleReset = () => {
      searchForm.session_id = null
      searchForm.keyword = ''
      pagination.page = 1
    }

    // 刷新
    const handleRefresh = () => {
      fetchData()
    }

    // Session 切换
    const handleSessionChange = () => {
      pagination.page = 1
    }

    // 新增
    const handleAdd = () => {
      isEdit.value = false
      resetForm()
      formVisible.value = true
    }

    // 编辑
    const handleEdit = (row) => {
      isEdit.value = true
      currentRecord.value = row
      Object.assign(form, {
        session_id: row.session_id,
        start_time: row.start_time ? row.start_time.substring(0, 5) : '',
        end_time: row.end_time ? row.end_time.substring(0, 5) : '',
        time_description: row.time_description || '',
        title: row.title,
        speaker_name: row.speaker_name || '',
        speaker_bio: row.speaker_bio || '',
        sort_order: row.sort_order || 0
      })
      formVisible.value = true
    }

    // 保存
    const handleSave = async () => {
      if (!formRef.value) return

      try {
        await formRef.value.validate()
        saving.value = true

        const formData = { ...form }
        let response

        if (isEdit.value) {
          response = await updateSchedule(currentRecord.value.id, formData)
        } else {
          response = await createSchedule(formData)
        }

        if (response.success) {
          ElMessage.success('保存成功')
          formVisible.value = false
          fetchData()
        } else {
          ElMessage.error(response.message || '保存失败')
        }
      } catch (error) {
        console.error('保存失败:', error)
        ElMessage.error('保存失败')
      } finally {
        saving.value = false
      }
    }

    // 删除
    const handleDelete = async (row) => {
      try {
        await ElMessageBox.confirm(
          '确定要删除该日程吗？',
          '警告',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )

        const response = await deleteSchedule(row.id)
        if (response.success) {
          ElMessage.success('删除成功')
          fetchData()
        } else {
          ElMessage.error(response.message || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error)
          ElMessage.error('删除失败')
        }
      }
    }

    // 重置表单
    const resetForm = () => {
      Object.assign(form, {
        session_id: searchForm.session_id,
        start_time: '',
        end_time: '',
        time_description: '',
        title: '',
        speaker_name: '',
        speaker_bio: '',
        sort_order: 0
      })
      if (formRef.value) {
        formRef.value.clearValidate()
      }
    }

    // 分页大小改变
    const handleSizeChange = (size) => {
      pagination.pageSize = size
      pagination.page = 1
    }

    // 当前页改变
    const handleCurrentChange = (page) => {
      pagination.page = page
    }

    // 格式化日期
    const formatDate = (dateString) => {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    }

    // 格式化日期时间
    const formatDateTime = (dateString) => {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    // 格式化时间
    const formatTime = (timeString) => {
      if (!timeString) return '-'
      return timeString.substring(0, 5)
    }

    // 组件挂载时获取数据
    onMounted(() => {
      fetchData()
    })

    return {
      loading,
      saving,
      tableData,
      sessionOptions,
      formVisible,
      detailVisible,
      isEdit,
      currentRecord,
      form,
      formRef,
      formRules,
      searchForm,
      pagination,
      filteredTableData,
      fetchData,
      handleSearch,
      handleReset,
      handleRefresh,
      handleSessionChange,
      handleAdd,
      handleEdit,
      handleSave,
      handleDelete,
      handleSizeChange,
      handleCurrentChange,
      getSessionTypeText,
      getSessionTypeTag,
      formatDate,
      formatDateTime,
      formatTime
    }
  }
}
</script>

<style scoped>
.admin-schedules {
  padding: 0;
}

.search-section,
.action-section,
.table-section {
  margin-bottom: 20px;
}

.search-form {
  padding: 10px 0;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.pagination-section {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

.session-date {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

.time-range {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-weight: 500;
}

.time-separator {
  color: #909399;
}

.time-desc {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

.title-text {
  font-weight: 500;
}

.speaker-bio {
  white-space: pre-wrap;
  line-height: 1.6;
  color: #606266;
}

.pre-wrap {
  white-space: pre-wrap;
  line-height: 1.5;
}
</style>
